import i18n, { t } from 'i18next'
import type { EnhancedProductFamilyId, EnhancedProductVariantId } from '#api/enhancements/enhancedTmsApiTypes'
import type { AdminNatCodesCode, AdminNatCodesFamily, AdminNatCodesGroup, FpcNationalCode, LocalizedString, ManCode } from '#api/tmsApiService'
import type { MarketDetails } from '#app/store/slices/configuration/marketSlice'
import type { StoredPartialConfiguration } from '#features/ManCode/mapTruckConfiguration'
import { deriveMarketLanguage } from '#features/NationalCodes/deriveMarketLanguage'
import { convertPrintOptionsFromManCode, convertPrintOptionsToManCode } from '#features/PrintOptions/convertPrintOptions'
import { translateBestEffort } from '#i18n/LocalizedString'
import type { TruckConfiguration } from '#store/slices/configurationSlice'
import type { MarketWithNationalCodes } from './loadNationalCodes'

export function exportNationalCodesForManCode(configuration: TruckConfiguration, marketDetails: MarketDetails | undefined): FpcNationalCode[] {
  const nationalCodes: FpcNationalCode[] = []
  if (!marketDetails) {
    return nationalCodes
  }

  const language = deriveMarketLanguage(configuration, marketDetails) ?? i18n.language
  const currency = marketDetails.currency ?? 'EUR'

  for (const group of marketDetails.groups) {
    if (!group.families) {
      continue
    }

    for (const family of group.families) {
      if (!family.codes) {
        continue
      }
      const configuredFamily = configuration.configuredFamilies[family.familyId as EnhancedProductFamilyId]
      if (!configuredFamily?.selectedVariant) {
        continue
      }

      const selectedCode = family.codes.find((code) => code.executionId === configuredFamily.selectedVariant?.id)
      if (!selectedCode) {
        continue
      }

      const printingOptions = configuredFamily.printOptions[selectedCode.executionId as EnhancedProductVariantId]

      const entry: FpcNationalCode = {
        variant: selectedCode.executionId ?? '',
        name: translateBestEffort(selectedCode.label, language),
        variantDescription: translateBestEffort(selectedCode.description, language),
        code: `${family.familyId ?? ''}${selectedCode.executionId ?? ''}`,

        family: family.familyId ?? '',
        familyDescription: [translateBestEffort(family.label, language), translateBestEffort(family.description, language)]
          .filter(Boolean)
          .join('\n'),

        grossListPrice: selectedCode.attributes?.grossListPrice,
        hideCode: selectedCode.attributes?.hideCode ?? false,
        withoutCode: (selectedCode.attributes?.withoutCode || selectedCode.executionId === 'Z') ?? false,
        printingOptions: printingOptions ? convertPrintOptionsToManCode(printingOptions) : selectedCode.attributes?.printingOptions,
        relevantForMaintenanceAndRepairBenefits: selectedCode.attributes?.relevantForMaintenanceAndRepairBenefits ?? false,
        relevantForResidualValue: selectedCode.attributes?.relevantForResidualValue ?? false,
        topOption: selectedCode.attributes?.topOption ?? false,
        classification: selectedCode.attributes?.classification,
        commissionRelevant: selectedCode.attributes?.commissionRelevant ?? false,
        condition: selectedCode.attributes?.condition ?? 'Z',
        currency,
        orderRelevant: selectedCode.attributes?.orderRelevant ?? false,
        selectionType: 'User',
      }
      nationalCodes.push(entry)
    }
  }

  return nationalCodes
}

//  simulate the removal of the national codes
const __debugRemoveAllNationalCodes = false

export function importConfiguredFamiliesFromManCode(
  marketData: MarketWithNationalCodes,
  manCode: ManCode,
  configuration: StoredPartialConfiguration | undefined,
  language: string,
) {
  if (!marketData) {
    return marketData
  }

  let localMarketData = marketData

  //  simulate the removal of the national codes
  if (__debugRemoveAllNationalCodes) {
    localMarketData = {
      ...marketData,
      groups: [],
    }
  }

  const groups: AdminNatCodesGroup[] = localMarketData.groups

  const missingEntriesGroup = {
    groupId: 'missing-entries',
    label: {
      de: t('configuration.national-codes.previous-selections.group.label', { defaultValue: 'Previous Selections', lng: 'de' }),
      en: t('configuration.national-codes.previous-selections.group.label', { defaultValue: 'Previous Selections', lng: 'en' }),
      fr: t('configuration.national-codes.previous-selections.group.label', { defaultValue: 'Previous Selections', lng: 'fr' }),
      it: t('configuration.national-codes.previous-selections.group.label', { defaultValue: 'Previous Selections', lng: 'it' }),
      // TODO add more when there are more languages
    },
    families: [] as AdminNatCodesFamily[],
  }

  const existingFamiliesByFamilyId: Partial<Record<EnhancedProductFamilyId, AdminNatCodesFamily>> = {}
  if (groups) {
    for (const group of groups) {
      if (!group.families) {
        continue
      }
      for (const family of group.families) {
        existingFamiliesByFamilyId[family.familyId as EnhancedProductFamilyId] = family
      }
    }
  }

  const nationalFPCCodes = manCode.configuration?.nationalFPCCodes
  if (!nationalFPCCodes) {
    return marketData
  }

  for (const nationalCode of nationalFPCCodes) {
    if (!nationalCode.code || !nationalCode.family || !nationalCode.variant) {
      continue
    }

    let existingFamily = existingFamiliesByFamilyId[nationalCode.family as EnhancedProductFamilyId]
    if (!existingFamily) {
      console.warn('Missing family for national code', nationalCode.code, 'family', nationalCode.family)
      const [label, ...descriptions] = nationalCode.familyDescription?.split('\n') || []
      const newFamily: AdminNatCodesFamily = {
        familyId: nationalCode.family,
        label: {
          [language]: label,
        } as LocalizedString,
        description: {
          [language]: descriptions.join('\n'),
        } as LocalizedString,
        codes: [],
      }
      missingEntriesGroup.families.push(newFamily)
      existingFamiliesByFamilyId[nationalCode.family as EnhancedProductFamilyId] = newFamily
      existingFamily = newFamily
    }

    let existingCode = existingFamily.codes?.find((code) => code.executionId === nationalCode.variant)
    if (!existingCode) {
      console.warn('Missing code for family', existingFamily.familyId, 'code', nationalCode.code, 'variant', nationalCode.variant)
      const newCode: AdminNatCodesCode = {
        codeId: nationalCode.code,
        executionId: nationalCode.variant,
        label: {
          [language]: nationalCode.name,
        } as LocalizedString,
        description: {
          [language]: nationalCode.variantDescription,
        } as LocalizedString,
        attributes: {
          grossListPrice: nationalCode.grossListPrice,
          hideCode: nationalCode.hideCode,
          withoutCode: nationalCode.withoutCode,
          printingOptions: nationalCode.printingOptions,
          relevantForMaintenanceAndRepairBenefits: nationalCode.relevantForMaintenanceAndRepairBenefits,
          relevantForResidualValue: nationalCode.relevantForResidualValue,
          topOption: nationalCode.topOption,
          classification: nationalCode.classification,
          commissionRelevant: nationalCode.commissionRelevant,
          condition: nationalCode.condition,
          orderRelevant: nationalCode.orderRelevant,
        },
      }
      existingFamily.codes = [...(existingFamily.codes || []), newCode]
      existingCode = newCode
    }

    if (configuration) {
      const selection = configuration.configuredFamilies[existingFamily.familyId as EnhancedProductFamilyId]
      if (!selection?.selectedVariant) {
        const printOptions = nationalCode.printingOptions

        configuration.configuredFamilies[existingFamily.familyId as EnhancedProductFamilyId] = {
          isLocked: false,
          printOptions: printOptions
            ? {
                [existingCode.executionId as EnhancedProductVariantId]: convertPrintOptionsFromManCode(printOptions),
              }
            : {},
          ...selection,
          selectedVariant: {
            id: existingCode.executionId as EnhancedProductVariantId,
            timestamp: Date.now(),
          },
        }
      }
    }
  }

  if (missingEntriesGroup.families.length > 0) {
    localMarketData = {
      ...marketData,
      groups: [...groups, missingEntriesGroup],
    }
    console.log('Updated market data with missing entries group', localMarketData)
  }

  return localMarketData
}
